/**
 * Step 2: Native calendar reminder
 *
 * Informational step reminding users to check their
 * device's native calendar app to ensure desired
 * calendars are synced and visible there.
 */

import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getTypographyStyle } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { OnboardingStepLayout } from '../OnboardingStepLayout';

type NativeCalendarReminderStepProps = {
  onContinue: () => void;
  onSkip?: () => void;
};

const REMINDER_ITEMS = [
  {
    icon: 'sync-outline' as const,
    text: 'Make sure your calendars are synced to your device',
  },
  {
    icon: 'eye-outline' as const,
    text: 'Check that the calendars you want are visible in your native calendar app',
  },
  {
    icon: 'phone-portrait-outline' as const,
    text: 'FimFam can only see calendars that are active on your device',
  },
] as const;

export function NativeCalendarReminderStep({
  onContinue,
  onSkip,
}: NativeCalendarReminderStepProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <OnboardingStepLayout
      icon={
        <Ionicons
          name="settings-outline"
          size={80}
          color={palette.primary}
        />
      }
      title="Check Your Device Calendars"
      subtitle="Before we continue, make sure your calendars are set up on your device."
      primaryButton={{
        label: 'Got it',
        onPress: onContinue,
      }}
      onSkip={onSkip}
    >
      <View style={styles.reminderList}>
        {REMINDER_ITEMS.map((item) => (
          <View
            key={item.icon}
            style={[
              styles.reminderItem,
              { backgroundColor: palette.cardBackground },
            ]}
          >
            <View
              style={[
                styles.iconCircle,
                { backgroundColor: palette.borderMuted },
              ]}
            >
              <Ionicons name={item.icon} size={22} color={palette.primary} />
            </View>
            <Text style={[styles.reminderText, { color: palette.text }]}>
              {item.text}
            </Text>
          </View>
        ))}
      </View>
    </OnboardingStepLayout>
  );
}

const bodyStyle = getTypographyStyle('body');

const styles = StyleSheet.create({
  reminderList: {
    paddingTop: Spacing.lg,
    gap: Spacing.md,
  },
  reminderItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: Spacing.lg,
    borderRadius: BorderRadius.md,
    gap: Spacing.md,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: BorderRadius.full,
    justifyContent: 'center',
    alignItems: 'center',
  },
  reminderText: {
    ...bodyStyle,
    fontSize: 14,
    lineHeight: 20,
    flex: 1,
  },
});
